import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx21I2vTurboParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 图像URL，用于图像转视频。可以是HTTP/HTTPS URL或Base64编码的图像数据。
     */
    img_url: string;
    /**
     * 文本提示词，描述要生成的视频内容，不超过800字符。
     * @length 0 - 800
     */
    prompt?: string;
  };
  /**
   * 参数设置
   */
  parameters?: {
    /**
     * 反向提示词，描述不希望出现在视频中的内容，不超过500字符。
     * @length 0 - 500
     */
    negative_prompt?: string;
    /**
     * 视频分辨率，支持480P或720P。
     * @default "720P"
     * @values 480P | 720P
     */
    resolution?: '480P' | '720P';
    /**
     * 视频时长，支持3、4或5秒。
     * @default 5
     * @values 3 | 4 | 5
     */
    duration?: 3 | 4 | 5;
    /**
     * 是否启用提示词扩展。
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印。
     * @default false
     */
    watermark?: boolean;
    /**
     * 随机种子，用于可重现的生成结果，范围0到2147483647。
     * @range 0 - 2147483647
     */
    seed?: number;
  };
}

export interface AlibabaWanx21I2vTurboResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx21_i2v_turbo
 *
 * 阿里云 Wanx2.1 I2V Turbo 图像生成视频接口
 * 
 * Model: wanx2.1-i2v-turbo (固定模型)
 * Features: 图像生成视频功能
 * Duration: 3、4或5秒 (默认5秒)
 * Resolution: 480P、720P (默认720P)
 * Advanced: 支持提示词、反向提示词、提示词扩展、水印设置、随机种子控制
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/image-to-video-api-reference
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx21I2vTurbo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx21I2vTurbo({
 *   // 参数见 AlibabaWanx21I2vTurboParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx21I2vTurboParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx21I2vTurbo(
  params: AlibabaWanx21I2vTurboParams,
): Promise<AlibabaWanx21I2vTurboResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx21_i2v_turbo',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx21I2vTurboResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
